export interface SessionTokens {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  tokenType?: string;
}

export interface AuthSessionSnapshot {
  tokens: SessionTokens;
  organizationId?: string;
  userId?: string;
  scopes: string[];
  impersonatorId?: string;
  lastRefreshedAt?: number;
  isAuthenticated: boolean;
  metadata?: Record<string, unknown>;
}

export type SessionListener = (snapshot: AuthSessionSnapshot) => void;

export interface SessionStorageAdapter {
  load: () => AuthSessionSnapshot | undefined;
  save: (snapshot: AuthSessionSnapshot) => void;
  clear: () => void;
}

const DEFAULT_SNAPSHOT: AuthSessionSnapshot = {
  tokens: {},
  scopes: [],
  isAuthenticated: false,
};

const SUBSCRIBERS = new Set<SessionListener>();
let adapter: SessionStorageAdapter | undefined = createBrowserStorageAdapter();
let snapshot: AuthSessionSnapshot = adapter?.load() ?? DEFAULT_SNAPSHOT;

function normalizeSnapshot(next: Partial<AuthSessionSnapshot>): AuthSessionSnapshot {
  const tokens = next.tokens ?? snapshot.tokens ?? {};
  const expiresAt = tokens.expiresAt ?? snapshot.tokens.expiresAt;
  const isAuthenticated = Boolean(tokens.accessToken) && (!expiresAt || expiresAt > Date.now());

  return {
    ...snapshot,
    ...next,
    tokens: {
      ...snapshot.tokens,
      ...tokens,
    },
    scopes: next.scopes ?? snapshot.scopes,
    isAuthenticated,
  };
}

function notifySubscribers() {
  SUBSCRIBERS.forEach((listener) => {
    listener(snapshot);
  });
}

function persistSnapshot() {
  if (!adapter) {
    return;
  }
  if (!snapshot.isAuthenticated) {
    adapter.clear();
    return;
  }
  adapter.save(snapshot);
}

export function getSessionSnapshot(): AuthSessionSnapshot {
  return snapshot;
}

export function setSessionSnapshot(update: Partial<AuthSessionSnapshot>): AuthSessionSnapshot {
  snapshot = normalizeSnapshot(update);
  persistSnapshot();
  notifySubscribers();
  return snapshot;
}

export function clearSession(): void {
  snapshot = { ...DEFAULT_SNAPSHOT, tokens: {} };
  adapter?.clear();
  notifySubscribers();
}

export function subscribeToSession(listener: SessionListener): () => void {
  SUBSCRIBERS.add(listener);
  listener(snapshot);
  return () => {
    SUBSCRIBERS.delete(listener);
  };
}

export function setSessionStorageAdapter(nextAdapter?: SessionStorageAdapter) {
  adapter = nextAdapter;
  if (adapter) {
    const restored = adapter.load();
    if (restored) {
      snapshot = normalizeSnapshot(restored);
    }
  }
}

export function buildAuthHeaders(
  currentSnapshot: AuthSessionSnapshot = snapshot
): Record<string, string> {
  if (!currentSnapshot.tokens.accessToken) {
    return {};
  }

  const type = currentSnapshot.tokens.tokenType ?? 'Bearer';
  return {
    Authorization: `${type} ${currentSnapshot.tokens.accessToken}`,
  };
}

function createBrowserStorageAdapter(): SessionStorageAdapter | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const storage = window.sessionStorage;
  if (!storage) {
    return undefined;
  }

  const STORAGE_KEY = '__frontendPlatformSession__';
  return {
    load: () => {
      try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) {
          return undefined;
        }
        return JSON.parse(raw) as AuthSessionSnapshot;
      } catch {
        storage.removeItem(STORAGE_KEY);
        return undefined;
      }
    },
    save: (value: AuthSessionSnapshot) => {
      storage.setItem(STORAGE_KEY, JSON.stringify(value));
    },
    clear: () => {
      storage.removeItem(STORAGE_KEY);
    },
  };
}
